import React from 'react';
import DOMPurify from 'dompurify';

interface EmailDisplayProps {
  content: string;
  subject?: string;
  className?: string;
  isOutbound?: boolean;
}

// Clean and prepare HTML email content for safe display
function prepareEmailHTML(rawContent: string): string {
  if (!rawContent) return "";

  let content = rawContent;

  // Remove MIME boundaries and headers
  content = content.replace(/--[a-zA-Z0-9_-]+[\s\S]*?--[a-zA-Z0-9_-]+--/g, "");
  content = content.replace(/Content-Type:.*$/gm, "");
  content = content.replace(/Content-Transfer-Encoding:.*$/gm, "");
  content = content.replace(/Content-Disposition:.*$/gm, "");

  // Remove base64 encoded attachments
  content = content.replace(/[A-Za-z0-9+/]{50,}={0,2}/g, "");

  // Decode common email encodings
  content = content
    .replace(/=3D/g, "=")
    .replace(/=20/g, " ")
    .replace(/=0A/g, "\n")
    .replace(/=0D/g, "\r")
    .replace(/=\r\n/g, "")
    .replace(/=([0-9A-F]{2})/gi, (_, hex) => {
      return String.fromCharCode(parseInt(hex, 16));
    });

  // Extract the main content and remove quoted replies
  const lines = content.split(/\r?\n/);
  const result: string[] = [];
  
  for (const line of lines) {
    // Stop at quoted content
    if (/^On .* wrote:/.test(line.trim()) || 
        line.trim().startsWith(">") || 
        line.trim().match(/^(-{2,}|\*{2,}|_{2,})/)) {
      break;
    }
    result.push(line);
  }

  content = result.join("\n").trim();

  // If it looks like plain text, wrap in basic HTML structure
  if (!content.includes('<html') && !content.includes('<div') && !content.includes('<p')) {
    content = content
      .replace(/\n\n/g, '</p><p>')
      .replace(/\n/g, '<br>');
    content = `<div style="font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif; line-height: 1.5;"><p>${content}</p></div>`;
  }

  return content;
}

export function EmailDisplay({ content, subject, className = "", isOutbound = false }: EmailDisplayProps) {
  // Prepare and sanitize the HTML content
  const htmlContent = prepareEmailHTML(content);
  const sanitizedHTML = DOMPurify.sanitize(htmlContent, {
    ALLOWED_TAGS: [
      'p', 'br', 'div', 'span', 'strong', 'b', 'em', 'i', 'u', 'a', 'ul', 'ol', 'li',
      'h1', 'h2', 'h3', 'h4', 'h5', 'h6', 'blockquote', 'table', 'tr', 'td', 'th',
      'thead', 'tbody', 'tfoot', 'img', 'hr'
    ],
    ALLOWED_ATTR: [
      'href', 'target', 'style', 'class', 'src', 'alt', 'width', 'height',
      'align', 'valign', 'colspan', 'rowspan'
    ],
    ALLOWED_URI_REGEXP: /^(?:(?:(?:f|ht)tps?|mailto|tel|callto|cid|xmpp):|[^a-z]|[a-z+.\-]+(?:[^a-z+.\-:]|$))/i
  });

  return (
    <div className={`email-display ${className}`}>
      {/* Email content */}
      <div 
        className="email-content max-w-none"
        style={{
          fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
          fontSize: '14px',
          lineHeight: '1.6',
          color: isOutbound ? '#ffffff' : '#1f2937'
        }}
        dangerouslySetInnerHTML={{ __html: sanitizedHTML }}
      />
    </div>
  );
} 